/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useCallback } from 'react';
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Printer, 
  Download, 
  CreditCard, 
  DollarSign, 
  Search, 
  RefreshCw, 
  Eye, 
  Send, 
  FileCheck, 
  Building2, 
  MapPin, 
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ShieldCheck,
  PenTool,
  Upload,
  X,
  FileCheck2,
  Receipt,
  Truck,
  Check,
  Sparkles,
  RotateCcw,
  Calendar,
  Filter,
  SlidersHorizontal
} from 'lucide-react';
import { PreInvoice, Folio, Client, isClientRole, isClientUser } from '../types.ts';
import { downloadPreInvoicePDF, downloadFolioPDF } from '../utils/pdfGenerator.ts';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  label?: string;
}

function BillingPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage = 10,
  onPageChange,
  label = 'registros'
}: PaginationProps) {
  if (totalItems === 0) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      
      if (currentPage < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="px-4 py-3 bg-slate-50/95 border-t border-slate-200/90 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs select-none">
      <div className="text-slate-600 font-medium flex items-center gap-1.5 flex-wrap">
        <span>Mostrando</span>
        <span className="font-black text-slate-900 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-2xs font-mono">{startItem} - {endItem}</span>
        <span>de</span>
        <span className="font-black text-slate-900 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-2xs font-mono">{totalItems}</span>
        <span>{label}</span>
        <span className="text-[10px] text-slate-400 font-semibold">(10 por página)</span>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
          title="Primera página"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center gap-1 font-bold shadow-2xs text-[11px]"
          title="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-1.5 text-slate-400 font-black select-none">
                  ...
                </span>
              );
            }
            const isCurrent = page === currentPage;
            return (
              <button
                type="button"
                key={`page-${page}`}
                onClick={() => onPageChange(Number(page))}
                className={`min-w-[30px] h-7 px-2 rounded-lg font-bold text-xs transition-all cursor-pointer shadow-2xs ${
                  isCurrent
                    ? 'bg-red-600 text-white shadow-xs font-black ring-2 ring-red-500/20'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center gap-1 font-bold shadow-2xs text-[11px]"
          title="Página siguiente"
        >
          <span className="hidden sm:inline">Siguiente</span>
          <ChevronRight className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-35 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
          title="Última página"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface BillingManagerProps {
  preInvoices: PreInvoice[];
  folios?: Folio[];
  clients?: Client[];
  activeClient?: Client;
  exchangeRate: number;
  onUpdateInvoice: (invoice: PreInvoice) => void;
  onUpdateFolio?: (folio: Folio) => void;
  currentUser: { role: string; name?: string; email?: string; clientCode?: string; assignedClientIds?: string[] };
  isClientViewOnly?: boolean;
}

export default function BillingManager({
  preInvoices,
  folios = [],
  clients = [],
  activeClient,
  exchangeRate,
  onUpdateInvoice,
  onUpdateFolio,
  currentUser,
  isClientViewOnly
}: BillingManagerProps) {
  const isUserClient = isClientUser(currentUser as any) || Boolean(isClientViewOnly);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoiceForDetail, setSelectedInvoiceForDetail] = useState<PreInvoice | null>(null);
  const [selectedFolioForDetail, setSelectedFolioForDetail] = useState<Folio | null>(null);
  const [isSatModalOpen, setIsSatModalOpen] = useState(false);
  const [signedInvoices, setSignedInvoices] = useState<Record<string, boolean>>({});
  const [sentInvoices, setSentInvoices] = useState<Record<string, boolean>>({});
  const [selectedLanguage, setSelectedLanguage] = useState('ES');

  // Smart filter states for GR Confirmation table
  const [grSearchQuery, setGrSearchQuery] = useState('');
  const [grFilterStatus, setGrFilterStatus] = useState<'ALL' | 'CURRENT_MONTH' | 'ROLLED_OVER' | 'CONFIRMED'>('ALL');
  const [grClientFilter, setGrClientFilter] = useState('ALL');

  // Date range filter states for GR Top Metric Cards
  type GrMetricDateFilter = 'ALL' | 'THIS_MONTH' | 'LAST_MONTH' | 'CUSTOM';
  const [pendingDateFilter, setPendingDateFilter] = useState<GrMetricDateFilter>('ALL');
  const [pendingCustomStart, setPendingCustomStart] = useState<string>('');
  const [pendingCustomEnd, setPendingCustomEnd] = useState<string>('');

  const [confirmedDateFilter, setConfirmedDateFilter] = useState<GrMetricDateFilter>('ALL');
  const [confirmedCustomStart, setConfirmedCustomStart] = useState<string>('');
  const [confirmedCustomEnd, setConfirmedCustomEnd] = useState<string>('');

  // Pagination states (10 items per page)
  const [grPage, setGrPage] = useState(1);
  const [invPage, setInvPage] = useState(1);
  const itemsPerPage = 10;

  // Month names in Spanish for labels
  const monthNamesEs = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const now = new Date();
  const currentMonthIdx = now.getMonth();
  const currentYearNum = now.getFullYear();
  const currentMonthLabel = `${monthNamesEs[currentMonthIdx]} ${currentYearNum}`;
  const prevMonthDate = new Date(currentYearNum, currentMonthIdx - 1, 1);
  const prevMonthLabel = `${monthNamesEs[prevMonthDate.getMonth()]} ${prevMonthDate.getFullYear()}`;

  // Helper to extract clean date components without timezone shifts
  const getFolioDateComp = useCallback((dateStr?: string) => {
    if (!dateStr) return null;
    const cleanStr = dateStr.substring(0, 10);
    const parts = cleanStr.split('-');
    if (parts.length === 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1; // 0-indexed
      const d = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return { year: y, month: m, day: d, ymd: cleanStr };
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), ymd: d.toISOString().substring(0, 10) };
    }
    return null;
  }, []);

  // Client context
  const clientName = activeClient?.name || (clients.length > 0 ? clients[0].name : 'BMW GROUP');
  const clientRfc = activeClient?.rfc || 'BMW980101XYZ';

  // Helper to compute exact folio amount
  const getFolioAmount = useCallback((f: Folio) => {
    let sumItems = 0;
    if (f.items && f.items.length > 0) {
      sumItems = f.items.reduce((s, item) => {
        const totalVal = Number(item.totalMXN);
        if (!isNaN(totalVal) && totalVal > 0) return s + totalVal;
        const unitP = Number(item.unitPriceMXN);
        const qty = Number(item.quantity) || 1;
        if (!isNaN(unitP) && unitP > 0) return s + (unitP * qty);
        return s;
      }, 0);
    }
    if (sumItems > 0) return sumItems;

    // Search matching preInvoice
    const matchingInv = preInvoices.find(inv => inv.folioId === f.id);
    if (matchingInv && Number(matchingInv.totalMXN) > 0) {
      return Number(matchingInv.totalMXN);
    }

    return 25000;
  }, [preInvoices]);

  // Filter delivered folios strictly ("Entrega en Planta" final phase)
  const deliveredFolios = useMemo(() => {
    return folios.filter(f => f.status === 'ENTREGA_PLANTA');
  }, [folios]);

  // Pending GR folios for top table
  const pendingGrFolios = useMemo(() => {
    return deliveredFolios.filter(f => f.grStatus !== 'CONFIRMADO');
  }, [deliveredFolios]);

  // Confirmed GR folios
  const confirmedGrFoliosList = useMemo(() => {
    return deliveredFolios.filter(f => f.grStatus === 'CONFIRMADO');
  }, [deliveredFolios]);

  // Filtered Pending GR folios for Metric Card 1
  const filteredPendingGrFolios = useMemo(() => {
    return pendingGrFolios.filter(f => {
      if (pendingDateFilter === 'ALL') return true;
      const dateStr = f.grBillingDate || f.shipmentDate || f.createdDate;
      const comp = getFolioDateComp(dateStr);
      if (!comp) return false;

      if (pendingDateFilter === 'THIS_MONTH') {
        return comp.year === currentYearNum && comp.month === currentMonthIdx;
      }
      if (pendingDateFilter === 'LAST_MONTH') {
        return comp.year === prevMonthDate.getFullYear() && comp.month === prevMonthDate.getMonth();
      }
      if (pendingDateFilter === 'CUSTOM') {
        if (pendingCustomStart && comp.ymd < pendingCustomStart) return false;
        if (pendingCustomEnd && comp.ymd > pendingCustomEnd) return false;
        return true;
      }
      return true;
    });
  }, [pendingGrFolios, pendingDateFilter, pendingCustomStart, pendingCustomEnd, currentYearNum, currentMonthIdx, prevMonthDate, getFolioDateComp]);

  // Filtered Confirmed GR folios for Metric Card 2
  const filteredConfirmedGrFolios = useMemo(() => {
    return confirmedGrFoliosList.filter(f => {
      if (confirmedDateFilter === 'ALL') return true;
      const dateStr = f.grBillingDate || f.grConfirmedDate || f.shipmentDate || f.createdDate;
      const comp = getFolioDateComp(dateStr);
      if (!comp) return false;

      if (confirmedDateFilter === 'THIS_MONTH') {
        return comp.year === currentYearNum && comp.month === currentMonthIdx;
      }
      if (confirmedDateFilter === 'LAST_MONTH') {
        return comp.year === prevMonthDate.getFullYear() && comp.month === prevMonthDate.getMonth();
      }
      if (confirmedDateFilter === 'CUSTOM') {
        if (confirmedCustomStart && comp.ymd < confirmedCustomStart) return false;
        if (confirmedCustomEnd && comp.ymd > confirmedCustomEnd) return false;
        return true;
      }
      return true;
    });
  }, [confirmedGrFoliosList, confirmedDateFilter, confirmedCustomStart, confirmedCustomEnd, currentYearNum, currentMonthIdx, prevMonthDate, getFolioDateComp]);

  // Calculate metrics according to GR workflow and active card date filters
  const totalGrPendiente = useMemo(() => {
    return filteredPendingGrFolios.reduce((sum, f) => sum + getFolioAmount(f), 0);
  }, [filteredPendingGrFolios, getFolioAmount]);

  const totalGrConfirmado = useMemo(() => {
    return filteredConfirmedGrFolios.reduce((sum, f) => sum + getFolioAmount(f), 0);
  }, [filteredConfirmedGrFolios, getFolioAmount]);

  // Unique clients for GR filter
  const uniqueGrClients = useMemo(() => {
    const map = new Map<string, string>();
    deliveredFolios.forEach(f => {
      if (f.clientName) {
        map.set(f.clientId || f.clientName, f.clientName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [deliveredFolios]);

  // Status counts for GR chips
  const grFilterCounts = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let allPending = 0;
    let currentMonthPending = 0;
    let rolledOverPending = 0;
    let confirmed = 0;

    deliveredFolios.forEach(folio => {
      const isConfirmed = folio.grStatus === 'CONFIRMADO';
      if (isConfirmed) {
        confirmed++;
      } else {
        allPending++;
        const origDateStr = folio.shipmentDate || folio.createdDate;
        if (origDateStr) {
          const d = new Date(origDateStr);
          if (!isNaN(d.getTime()) && (d.getFullYear() < currentYear || (d.getFullYear() === currentYear && d.getMonth() < currentMonth))) {
            rolledOverPending++;
          } else {
            currentMonthPending++;
          }
        } else {
          currentMonthPending++;
        }
      }
    });

    return {
      allPending,
      currentMonthPending,
      rolledOverPending,
      confirmed,
      totalDelivered: deliveredFolios.length
    };
  }, [deliveredFolios]);

  // Smart Filtered GR Folios
  const filteredGrFolios = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return deliveredFolios.filter((folio) => {
      const isConfirmed = folio.grStatus === 'CONFIRMADO';
      const origDateStr = folio.shipmentDate || folio.createdDate;
      let isRolledOver = false;
      if (origDateStr && !isConfirmed) {
        const d = new Date(origDateStr);
        if (!isNaN(d.getTime()) && (d.getFullYear() < currentYear || (d.getFullYear() === currentYear && d.getMonth() < currentMonth))) {
          isRolledOver = true;
        }
      }

      // Filter by Status Tab
      if (grFilterStatus === 'ALL') {
        if (isConfirmed) return false;
      } else if (grFilterStatus === 'CURRENT_MONTH') {
        if (isConfirmed || isRolledOver) return false;
      } else if (grFilterStatus === 'ROLLED_OVER') {
        if (isConfirmed || !isRolledOver) return false;
      } else if (grFilterStatus === 'CONFIRMED') {
        if (!isConfirmed) return false;
      }

      // Filter by Client
      if (grClientFilter !== 'ALL') {
        if (folio.clientId !== grClientFilter && folio.clientName !== grClientFilter) {
          return false;
        }
      }

      // Intelligent Search Query filter
      if (grSearchQuery.trim()) {
        const q = grSearchQuery.toLowerCase().trim();
        const matchId = folio.id?.toLowerCase().includes(q);
        const matchClient = (folio.clientName || '').toLowerCase().includes(q);
        const matchDest = (folio.destination || '').toLowerCase().includes(q);
        const matchPo = (folio.poNumber || '').toLowerCase().includes(q);
        const matchDriver = (folio.driverName || '').toLowerCase().includes(q);
        const matchTruck = (folio.truckPlates || '').toLowerCase().includes(q);
        const matchBillingDate = (folio.grBillingDate || '').toLowerCase().includes(q);
        const matchShipDate = (folio.shipmentDate || '').toLowerCase().includes(q);
        const matchCreateDate = (folio.createdDate || '').toLowerCase().includes(q);
        const matchItems = folio.items?.some(it => 
          it.description?.toLowerCase().includes(q) || 
          (it.partNumber && it.partNumber.toLowerCase().includes(q))
        );
        const folioAmt = getFolioAmount(folio);
        const matchAmount = folioAmt.toString().includes(q) || folioAmt.toLocaleString('es-MX').includes(q);

        if (!matchId && !matchClient && !matchDest && !matchPo && !matchDriver && !matchTruck && !matchBillingDate && !matchShipDate && !matchCreateDate && !matchItems && !matchAmount) {
          return false;
        }
      }

      return true;
    });
  }, [deliveredFolios, grFilterStatus, grClientFilter, grSearchQuery, getFolioAmount]);

  // Confirmed GR items for bottom table (ONLY confirmed folios in ENTREGA_PLANTA pass to this table)
  const confirmedGrItems = useMemo(() => {
    const confirmedFolios = folios.filter(f => f.status === 'ENTREGA_PLANTA' && f.grStatus === 'CONFIRMADO');
    const items: PreInvoice[] = [];
    const processedFolioIds = new Set<string>();

    // 1. Include preInvoices that belong to confirmed folios in ENTREGA_PLANTA status
    preInvoices.forEach(inv => {
      const relatedFolio = folios.find(f => f.id === inv.folioId);
      const isDeliveredAtPlant = relatedFolio ? relatedFolio.status === 'ENTREGA_PLANTA' : true;
      const isGrConfirmed = relatedFolio ? relatedFolio.grStatus === 'CONFIRMADO' : (inv.status === 'Paid');

      if (isDeliveredAtPlant && isGrConfirmed) {
        const folioTotal = relatedFolio ? getFolioAmount(relatedFolio) : inv.totalMXN;

        items.push({
          ...inv,
          totalMXN: folioTotal > 0 ? folioTotal : inv.totalMXN,
          status: 'Paid',
        });
        if (inv.folioId) {
          processedFolioIds.add(inv.folioId);
        }
      }
    });

    // 2. Include confirmed folios that do not have an explicit preInvoice entry yet
    confirmedFolios.forEach(folio => {
      if (!processedFolioIds.has(folio.id)) {
        const totalAmount = getFolioAmount(folio);
        const subtotal = totalAmount > 0 ? totalAmount / 1.16 : 21551.72;
        const tax = totalAmount > 0 ? totalAmount - subtotal : 3448.28;
        const finalTotal = totalAmount > 0 ? totalAmount : 25000;
        const fx = exchangeRate || 19.5;

        items.push({
          id: `REC-${folio.id.replace(/\s+/g, '-')}`,
          folioId: folio.id,
          clientId: folio.clientId || activeClient?.id || 'CLIENT-1',
          clientName: folio.clientName || clientName,
          issueDate: new Date().toISOString().split('T')[0],
          subtotalMXN: Math.round(subtotal * 100) / 100,
          subtotalUSD: Math.round((subtotal / fx) * 100) / 100,
          taxMXN: Math.round(tax * 100) / 100,
          taxUSD: Math.round((tax / fx) * 100) / 100,
          totalMXN: Math.round(finalTotal * 100) / 100,
          totalUSD: Math.round((finalTotal / fx) * 100) / 100,
          status: 'Paid',
          items: folio.items.length > 0 ? folio.items.map(it => ({
            description: it.description,
            quantity: it.quantity,
            priceMXN: Number(it.unitPriceMXN) || (it.totalMXN ? Number(it.totalMXN) / it.quantity : 1000),
            priceUSD: ((Number(it.unitPriceMXN) || 1000) / fx).toFixed(2),
            totalMXN: Number(it.totalMXN) || 0,
            totalUSD: ((Number(it.totalMXN) || 0) / fx).toFixed(2)
          })) : [
            {
              description: 'Servicio de Decapado Industrial y Flete de Retorno',
              quantity: 1,
              priceMXN: Math.round(subtotal * 100) / 100,
              priceUSD: (subtotal / fx).toFixed(2),
              totalMXN: Math.round(subtotal * 100) / 100,
              totalUSD: (subtotal / fx).toFixed(2)
            }
          ]
        });
      }
    });

    return items;
  }, [preInvoices, folios, activeClient, clientName, exchangeRate, getFolioAmount]);

  // Filtered confirmed items with search query
  const filteredInvoices = useMemo(() => {
    return confirmedGrItems.filter(inv => {
      const matchesSearch = 
        inv.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.folioId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.clientName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [confirmedGrItems, searchQuery]);

  // Paginated collections (10 items per page)
  const totalGrPages = Math.max(1, Math.ceil(filteredGrFolios.length / itemsPerPage));
  const paginatedGrFolios = useMemo(() => {
    const start = (grPage - 1) * itemsPerPage;
    return filteredGrFolios.slice(start, start + itemsPerPage);
  }, [filteredGrFolios, grPage, itemsPerPage]);

  const totalInvPages = Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage));
  const paginatedInvoices = useMemo(() => {
    const start = (invPage - 1) * itemsPerPage;
    return filteredInvoices.slice(start, start + itemsPerPage);
  }, [filteredInvoices, invPage, itemsPerPage]);

  // Handlers
  const handleToggleGrStatus = (folio: Folio, customDate?: string) => {
    const isCurrentlyConfirmed = folio.grStatus === 'CONFIRMADO';
    const newGrStatus: 'PENDIENTE' | 'CONFIRMADO' = isCurrentlyConfirmed ? 'PENDIENTE' : 'CONFIRMADO';
    const todayStr = new Date().toISOString().split('T')[0];
    const resolvedBillingDate = customDate || folio.grBillingDate || (folio.shipmentDate || folio.createdDate || '').substring(0, 10) || todayStr;
    
    const updatedFolio: Folio = {
      ...folio,
      grStatus: newGrStatus,
      grBillingDate: newGrStatus === 'CONFIRMADO' ? resolvedBillingDate : folio.grBillingDate,
      grConfirmedDate: newGrStatus === 'CONFIRMADO' ? new Date().toISOString() : undefined,
    };

    if (onUpdateFolio) {
      onUpdateFolio(updatedFolio);
    }

    if (selectedFolioForDetail && selectedFolioForDetail.id === folio.id) {
      setSelectedFolioForDetail(updatedFolio);
    }
  };

  const handleUpdateGrBillingDate = (folio: Folio, newDate: string) => {
    const updatedFolio: Folio = {
      ...folio,
      grBillingDate: newDate,
    };

    if (onUpdateFolio) {
      onUpdateFolio(updatedFolio);
    }

    if (selectedFolioForDetail && selectedFolioForDetail.id === folio.id) {
      setSelectedFolioForDetail(updatedFolio);
    }
  };

  const handleUpdateFolioStatus = (folio: Folio, newStatus: string) => {
    const updatedFolio: Folio = {
      ...folio,
      status: newStatus as any,
    };
    if (onUpdateFolio) {
      onUpdateFolio(updatedFolio);
    }
    if (selectedFolioForDetail && selectedFolioForDetail.id === folio.id) {
      setSelectedFolioForDetail(updatedFolio);
    }
  };

  const handleGeneratePreInvoice = (folio: Folio) => {
    const totalAmount = folio.items.reduce((s, it) => s + (Number(it.totalMXN) || 0), 0);
    const subtotal = totalAmount > 0 ? totalAmount / 1.16 : 21551.72;
    const tax = totalAmount > 0 ? totalAmount - subtotal : 3448.28;
    const finalTotal = totalAmount > 0 ? totalAmount : 25000;
    const fx = exchangeRate || 19.5;

    const newInvoice: PreInvoice = {
      id: `PRE-${folio.id.replace(/\s+/g, '-')}`,
      folioId: folio.id,
      clientId: folio.clientId || activeClient?.id || 'CLIENT-1',
      clientName: folio.clientName || clientName,
      issueDate: new Date().toISOString().split('T')[0],
      subtotalMXN: Math.round(subtotal * 100) / 100,
      subtotalUSD: Math.round((subtotal / fx) * 100) / 100,
      taxMXN: Math.round(tax * 100) / 100,
      taxUSD: Math.round((tax / fx) * 100) / 100,
      totalMXN: Math.round(finalTotal * 100) / 100,
      totalUSD: Math.round((finalTotal / fx) * 100) / 100,
      status: 'Draft',
      items: folio.items.length > 0 ? folio.items.map(it => {
        const itemPriceMXN = Number(it.unitPriceMXN) || (it.totalMXN ? Number(it.totalMXN) / it.quantity : 1000);
        const itemTotalMXN = Number(it.totalMXN) || (itemPriceMXN * it.quantity);
        return {
          description: it.description,
          quantity: it.quantity,
          priceMXN: itemPriceMXN,
          priceUSD: (itemPriceMXN / fx).toFixed(2),
          totalMXN: itemTotalMXN,
          totalUSD: (itemTotalMXN / fx).toFixed(2),
        };
      }) : [
        {
          description: 'Flete de Retorno / Bienes Entregados en Planta',
          quantity: 1,
          priceMXN: Math.round(subtotal * 100) / 100,
          priceUSD: (subtotal / fx).toFixed(2),
          totalMXN: Math.round(subtotal * 100) / 100,
          totalUSD: (subtotal / fx).toFixed(2),
        }
      ]
    };

    onUpdateInvoice(newInvoice);
  };

  const handleSignInvoice = (invoiceId: string) => {
    setSignedInvoices(prev => ({ ...prev, [invoiceId]: true }));
  };

  const handleSendInvoice = (invoiceId: string) => {
    setSentInvoices(prev => ({ ...prev, [invoiceId]: true }));
    const inv = preInvoices.find(i => i.id === invoiceId);
    if (inv && inv.status === 'Draft') {
      onUpdateInvoice({ ...inv, status: 'Sent' });
    }
  };

  const handleEmitInvoice = (inv: PreInvoice) => {
    const updated: PreInvoice = { ...inv, status: 'Sent' };
    onUpdateInvoice(updated);
    alert(`Factura CFDI ${inv.id} emitida con éxito ante el SAT.`);
  };

  return (
    <div className="space-y-6 pb-12 font-sans" id="mis-facturas-cobros-container">

      {/* METRIC CARDS GRID - 2 CARDS: GR PENDIENTE & GR CONFIRMADO */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        
        {/* CARD 1: GR PENDIENTE */}
        <div className="bg-white border-2 border-amber-400/80 shadow-xs rounded-2xl p-4.5 space-y-2 relative overflow-hidden flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-black uppercase text-amber-700 tracking-wider flex items-center gap-1.5 shrink-0">
                <Clock className="h-4 w-4 text-amber-600 animate-pulse" />
                <span>GR POR FACTURAR</span>
              </p>

              {/* Discreet Date Range Selector & Folio Count */}
              <div className="flex items-center gap-1.5 ml-auto">
                <div className="relative inline-flex items-center bg-amber-50/90 hover:bg-amber-100/90 border border-amber-200 text-amber-900 rounded-lg px-2 py-0.5 text-[10px] font-bold shadow-2xs transition-colors">
                  <Calendar className="h-3 w-3 mr-1 text-amber-600 shrink-0" />
                  <select
                    value={pendingDateFilter}
                    onChange={(e) => setPendingDateFilter(e.target.value as GrMetricDateFilter)}
                    className="bg-transparent text-amber-900 font-bold focus:outline-none cursor-pointer pr-1 text-[10px]"
                    title="Filtrar GRs por facturar por rango de fechas"
                  >
                    <option value="ALL">Todo el Histórico</option>
                    <option value="THIS_MONTH">Mes Actual ({currentMonthLabel})</option>
                    <option value="LAST_MONTH">Mes Anterior ({prevMonthLabel})</option>
                    <option value="CUSTOM">Rango Personalizado...</option>
                  </select>
                </div>

                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 border border-amber-300 font-mono shrink-0">
                  {filteredPendingGrFolios.length} {filteredPendingGrFolios.length === 1 ? 'folio' : 'folios'}
                </span>
              </div>
            </div>

            {/* Custom Date Range Picker (discreet inline bar) */}
            {pendingDateFilter === 'CUSTOM' && (
              <div className="flex flex-wrap items-center gap-1.5 p-1.5 bg-amber-50/80 border border-amber-200/90 rounded-lg text-[10px] text-amber-900">
                <span className="font-bold text-[10px] text-amber-700">Del:</span>
                <input
                  type="date"
                  value={pendingCustomStart}
                  onChange={(e) => setPendingCustomStart(e.target.value)}
                  className="bg-white border border-amber-300 rounded px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-500 shadow-2xs cursor-pointer"
                />
                <span className="font-bold text-[10px] text-amber-700">Al:</span>
                <input
                  type="date"
                  value={pendingCustomEnd}
                  onChange={(e) => setPendingCustomEnd(e.target.value)}
                  className="bg-white border border-amber-300 rounded px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-500 shadow-2xs cursor-pointer"
                />
                {(pendingCustomStart || pendingCustomEnd) && (
                  <button
                    type="button"
                    onClick={() => { setPendingCustomStart(''); setPendingCustomEnd(''); }}
                    className="text-amber-800 hover:text-amber-950 text-[10px] underline ml-auto font-bold cursor-pointer"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}

            <div className="flex items-baseline gap-1.5 pt-0.5">
              <span className="text-2xl font-black text-slate-900 tracking-tight font-mono">
                ${totalGrPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-xs font-bold text-amber-600">MXN</span>
            </div>
          </div>

          <p className="text-[10px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            {pendingDateFilter === 'ALL' && 'Monto acumulado histórico de folios por facturar (GR)'}
            {pendingDateFilter === 'THIS_MONTH' && `Monto de folios por facturar correspondientes a ${currentMonthLabel}`}
            {pendingDateFilter === 'LAST_MONTH' && `Monto de folios por facturar correspondientes a ${prevMonthLabel}`}
            {pendingDateFilter === 'CUSTOM' && (
              pendingCustomStart || pendingCustomEnd
                ? `Monto de folios por facturar del ${pendingCustomStart || 'inicio'} al ${pendingCustomEnd || 'hoy'}`
                : 'Monto de folios por facturar en rango seleccionado'
            )}
          </p>
        </div>

        {/* CARD 2: GR CONFIRMADO */}
        <div className="bg-white border-2 border-emerald-500/80 shadow-xs rounded-2xl p-4.5 space-y-2 relative overflow-hidden flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-black uppercase text-emerald-700 tracking-wider flex items-center gap-1.5 shrink-0">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>GR FACTURADO</span>
              </p>

              {/* Discreet Date Range Selector & Folio Count */}
              <div className="flex items-center gap-1.5 ml-auto">
                <div className="relative inline-flex items-center bg-emerald-50/90 hover:bg-emerald-100/90 border border-emerald-200 text-emerald-900 rounded-lg px-2 py-0.5 text-[10px] font-bold shadow-2xs transition-colors">
                  <Calendar className="h-3 w-3 mr-1 text-emerald-600 shrink-0" />
                  <select
                    value={confirmedDateFilter}
                    onChange={(e) => setConfirmedDateFilter(e.target.value as GrMetricDateFilter)}
                    className="bg-transparent text-emerald-900 font-bold focus:outline-none cursor-pointer pr-1 text-[10px]"
                    title="Filtrar GRs facturados por rango de fechas"
                  >
                    <option value="ALL">Todo el Histórico</option>
                    <option value="THIS_MONTH">Mes Actual ({currentMonthLabel})</option>
                    <option value="LAST_MONTH">Mes Anterior ({prevMonthLabel})</option>
                    <option value="CUSTOM">Rango Personalizado...</option>
                  </select>
                </div>

                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono shrink-0">
                  {filteredConfirmedGrFolios.length} {filteredConfirmedGrFolios.length === 1 ? 'folio' : 'folios'}
                </span>
              </div>
            </div>

            {/* Custom Date Range Picker (discreet inline bar) */}
            {confirmedDateFilter === 'CUSTOM' && (
              <div className="flex flex-wrap items-center gap-1.5 p-1.5 bg-emerald-50/80 border border-emerald-200/90 rounded-lg text-[10px] text-emerald-900">
                <span className="font-bold text-[10px] text-emerald-700">Del:</span>
                <input
                  type="date"
                  value={confirmedCustomStart}
                  onChange={(e) => setConfirmedCustomStart(e.target.value)}
                  className="bg-white border border-emerald-300 rounded px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 shadow-2xs cursor-pointer"
                />
                <span className="font-bold text-[10px] text-emerald-700">Al:</span>
                <input
                  type="date"
                  value={confirmedCustomEnd}
                  onChange={(e) => setConfirmedCustomEnd(e.target.value)}
                  className="bg-white border border-emerald-300 rounded px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 shadow-2xs cursor-pointer"
                />
                {(confirmedCustomStart || confirmedCustomEnd) && (
                  <button
                    type="button"
                    onClick={() => { setConfirmedCustomStart(''); setConfirmedCustomEnd(''); }}
                    className="text-emerald-800 hover:text-emerald-950 text-[10px] underline ml-auto font-bold cursor-pointer"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}

            <div className="flex items-baseline gap-1.5 pt-0.5">
              <span className="text-2xl font-black text-slate-900 tracking-tight font-mono">
                ${totalGrConfirmado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-xs font-bold text-emerald-600">MXN</span>
            </div>
          </div>

          <p className="text-[10px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            {confirmedDateFilter === 'ALL' && 'Monto acumulado histórico de folios facturados con recepción (GR)'}
            {confirmedDateFilter === 'THIS_MONTH' && `Monto de folios facturados correspondientes a ${currentMonthLabel}`}
            {confirmedDateFilter === 'LAST_MONTH' && `Monto de folios facturados correspondientes a ${prevMonthLabel}`}
            {confirmedDateFilter === 'CUSTOM' && (
              confirmedCustomStart || confirmedCustomEnd
                ? `Monto de folios facturados del ${confirmedCustomStart || 'inicio'} al ${confirmedCustomEnd || 'hoy'}`
                : 'Monto de folios facturados en rango seleccionado'
            )}
          </p>
        </div>

      </div>

      {/* MIDDLE SECTION: CONFIRMACIÓN DE GR (FOLIOS ENTREGADOS EN PLANTA) - EXCLUSIVE FOR INTERNAL OPERATORS/ADMINS */}
      {!isUserClient && (
      <div className="rounded-2xl overflow-hidden border border-slate-200/80 shadow-sm bg-white">
        
        {/* Banner dark header */}
        <div className="bg-[#27272a] text-white p-4 sm:p-4.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-950/80 border border-red-800 text-red-400 rounded-xl">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-black tracking-tight text-white flex items-center gap-2 uppercase">
                Confirmación de GR (Folios Entregados en Planta)
              </h2>
              <p className="text-xs text-slate-300 font-medium mt-0.5">
                Los folios en fase de entrega en planta ingresan como <strong className="text-amber-300">GR POR FACTURAR</strong>. Seleccione la casilla (Checkbox) para validar directamente a <strong className="text-emerald-300">GR FACTURADO</strong> y finalizar el proceso.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-amber-950/80 text-amber-200 border border-amber-800 font-mono text-xs font-black px-3.5 py-1.5 rounded-full shrink-0 shadow-xs flex items-center gap-1">
              <span>GRs Por Facturar: {deliveredFolios.filter(f => f.grStatus !== 'CONFIRMADO').length}</span>
              <span className="text-amber-400 font-extrabold">(${totalGrPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)</span>
            </div>
            <div className="bg-emerald-950/80 text-emerald-200 border border-emerald-800 font-mono text-xs font-black px-3.5 py-1.5 rounded-full shrink-0 shadow-xs flex items-center gap-1">
              <span>GRs Facturados: {deliveredFolios.filter(f => f.grStatus === 'CONFIRMADO').length}</span>
              <span className="text-emerald-400 font-extrabold">(${totalGrConfirmado.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)</span>
            </div>
          </div>
        </div>

        {/* Smart Search & Filter Bar for GR Confirmation Table */}
        <div className="p-3.5 bg-slate-50 border-b border-slate-200/80 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          
          {/* Intelligent Search Input */}
          <div className="relative flex-1 min-w-[280px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={grSearchQuery}
              onChange={(e) => {
                setGrSearchQuery(e.target.value);
                setGrPage(1);
              }}
              placeholder="Filtro inteligente: buscar por folio, cliente, planta, mercancía, PO, fecha..."
              className="w-full bg-white pl-10 pr-9 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-2xs transition-all"
            />
            {grSearchQuery && (
              <button
                type="button"
                onClick={() => {
                  setGrSearchQuery('');
                  setGrPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
                title="Limpiar búsqueda"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Quick Filter Chips & Client Selector */}
          <div className="flex flex-wrap items-center gap-2">
            
            {/* Quick Status Chips */}
            <div className="inline-flex p-1 bg-white border border-slate-200 rounded-xl shadow-2xs gap-1">
              <button
                type="button"
                onClick={() => {
                  setGrFilterStatus('ALL');
                  setGrPage(1);
                }}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                  grFilterStatus === 'ALL'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
                title="Mostrar todos los folios con GR por facturar"
              >
                Por Facturar ({grFilterCounts.allPending})
              </button>

              <button
                type="button"
                onClick={() => {
                  setGrFilterStatus('CURRENT_MONTH');
                  setGrPage(1);
                }}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  grFilterStatus === 'CURRENT_MONTH'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50'
                }`}
                title="Mostrar folios generados en el mes en curso"
              >
                <span>Mes Actual</span>
                <span className="font-mono text-[10px]">({grFilterCounts.currentMonthPending})</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setGrFilterStatus('ROLLED_OVER');
                  setGrPage(1);
                }}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  grFilterStatus === 'ROLLED_OVER'
                    ? 'bg-amber-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-amber-800 hover:bg-amber-50'
                }`}
                title="Mostrar folios que quedaron pendientes de meses anteriores"
              >
                <span>⚠️ Arrastrados</span>
                <span className="font-mono text-[10px]">({grFilterCounts.rolledOverPending})</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setGrFilterStatus('CONFIRMED');
                  setGrPage(1);
                }}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  grFilterStatus === 'CONFIRMED'
                    ? 'bg-teal-700 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-teal-800 hover:bg-teal-50'
                }`}
                title="Ver folios ya facturados"
              >
                <CheckCircle2 className="h-3 w-3" />
                <span>Facturados</span>
                <span className="font-mono text-[10px]">({grFilterCounts.confirmed})</span>
              </button>
            </div>

            {/* Client Filter Dropdown if more than 1 client */}
            {uniqueGrClients.length > 1 && (
              <div className="flex items-center gap-1 bg-white border border-slate-200 px-2.5 py-1 rounded-xl shadow-2xs text-[11px]">
                <Building2 className="h-3.5 w-3.5 text-slate-400" />
                <select
                  value={grClientFilter}
                  onChange={(e) => {
                    setGrClientFilter(e.target.value);
                    setGrPage(1);
                  }}
                  className="bg-transparent text-slate-700 font-bold text-xs focus:outline-none cursor-pointer pr-1"
                >
                  <option value="ALL">Todos los clientes</option>
                  {uniqueGrClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Reset Filters button if search/filter applied */}
            {(grSearchQuery || grFilterStatus !== 'ALL' || grClientFilter !== 'ALL') && (
              <button
                type="button"
                onClick={() => {
                  setGrSearchQuery('');
                  setGrFilterStatus('ALL');
                  setGrClientFilter('ALL');
                  setGrPage(1);
                }}
                className="px-2.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[11px] rounded-xl transition-all flex items-center gap-1 cursor-pointer"
                title="Restablecer filtros de búsqueda"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Limpiar</span>
              </button>
            )}

          </div>

        </div>

        {/* Table of folios delivered in plant */}
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 z-10 shadow-2xs">
              <tr className="bg-slate-100/95 backdrop-blur-xs border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                <th className="py-3 px-4 text-center">Validar GR</th>
                <th className="py-3 px-4">Folio / Guía</th>
                <th className="py-3 px-4">Cliente / Planta Destino</th>
                <th className="py-3 px-4">Fecha Facturación GR</th>
                <th className="py-3 px-4">Descripción Mercancía</th>
                <th className="py-3 px-4 text-center">Peso Kg</th>
                <th className="py-3 px-4 text-right">Monto Estimado</th>
                <th className="py-3 px-4 text-center">Estado GR</th>
                <th className="py-3 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {paginatedGrFolios.length > 0 ? (
                paginatedGrFolios.map((folio) => {
                  const isConfirmed = folio.grStatus === 'CONFIRMADO';
                  const totalEst = getFolioAmount(folio);
                  const now = new Date();
                  const currentMonth = now.getMonth();
                  const currentYear = now.getFullYear();
                  const origDateStr = folio.shipmentDate || folio.createdDate;
                  let isRolledOver = false;
                  let origMonthLabel = '';
                  if (origDateStr && !isConfirmed) {
                    const d = new Date(origDateStr);
                    if (!isNaN(d.getTime()) && (d.getFullYear() < currentYear || (d.getFullYear() === currentYear && d.getMonth() < currentMonth))) {
                      isRolledOver = true;
                      origMonthLabel = d.toLocaleString('es-MX', { month: 'short', year: 'numeric' });
                    }
                  }
                  
                  return (
                    <tr key={folio.id} className={`transition-colors ${isConfirmed ? 'bg-emerald-50/40' : isRolledOver ? 'bg-amber-50/20 hover:bg-amber-50/40' : 'hover:bg-slate-50/60'}`}>
                      
                      {/* Checkbox for direct GR confirmation */}
                      <td className="py-3.5 px-4 text-center">
                        <label className="inline-flex items-center gap-2 cursor-pointer p-1.5 rounded-xl hover:bg-slate-200/60 transition-all border border-slate-200/80 bg-white shadow-2xs">
                          <input
                            type="checkbox"
                            checked={isConfirmed}
                            onChange={() => handleToggleGrStatus(folio)}
                            className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-600"
                          />
                          <span className={`text-[10px] font-black uppercase select-none ${isConfirmed ? 'text-emerald-700' : 'text-slate-600'}`}>
                            {isConfirmed ? 'FACTURADO' : 'VALIDAR'}
                          </span>
                        </label>
                      </td>

                      <td className="py-3.5 px-4 font-black text-slate-900">
                        <div>{folio.id}</div>
                        <div className="text-[10px] text-slate-400 font-semibold font-mono">
                          ENTREGA PLANTA
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900">{folio.clientName}</div>
                        <div className="text-[10px] text-slate-400 font-semibold">
                          Destino: {folio.destination || 'PLANTA'}
                        </div>
                      </td>

                      {/* Fecha Facturación GR */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="date"
                              value={folio.grBillingDate || (folio.shipmentDate || folio.createdDate || '').substring(0, 10) || new Date().toISOString().split('T')[0]}
                              onChange={(e) => handleUpdateGrBillingDate(folio, e.target.value)}
                              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-[11px] font-mono font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-2xs cursor-pointer"
                              title="Fecha a la que corresponde el GR para su facturación"
                            />
                          </div>
                          {isRolledOver && (
                            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 text-[9px] font-black uppercase">
                              <span>⚠️ De {origMonthLabel} (Por Facturar)</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 max-w-md">
                        {folio.items.map(it => `${it.quantity}x ${it.description}`).join(', ') || 'Piezas procesadas'}
                      </td>

                      <td className="py-3.5 px-4 text-center text-slate-400 font-bold">
                        {folio.totalWeightKg ? `${folio.totalWeightKg} kg` : 'N/D'}
                      </td>

                      <td className="py-3.5 px-4 text-right font-black text-slate-900">
                        ${totalEst.toLocaleString('es-MX', { minimumFractionDigits: 2 })} <span className="text-[10px] font-bold text-slate-400">MXN</span>
                      </td>

                      <td className="py-3.5 px-4 text-center font-mono">
                        {isConfirmed ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-900 border border-emerald-300 uppercase shadow-2xs">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            <span>GR FACTURADO (${totalEst.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-amber-100 text-amber-900 border border-amber-300 uppercase shadow-2xs">
                            <Clock className="h-3.5 w-3.5 text-amber-600 animate-pulse" />
                            <span>GR POR FACTURAR (${totalEst.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)</span>
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedFolioForDetail(folio)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-slate-200/80"
                            title="Ver detalle completo del folio"
                          >
                            <Eye className="h-3.5 w-3.5 text-slate-500" />
                            <span>Detalle</span>
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-500 font-medium">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="p-3 bg-slate-100 rounded-full text-slate-400">
                        <Search className="h-5 w-5" />
                      </div>
                      <p className="text-xs font-bold text-slate-700">
                        {grSearchQuery ? `No se encontraron folios para "${grSearchQuery}"` : 'No hay folios con los criterios seleccionados.'}
                      </p>
                      <p className="text-[11px] text-slate-400">
                        {grSearchQuery
                          ? 'Pruebe buscando por otro término como número de folio, planta o descripción de material.'
                          : 'Pruebe seleccionando otra pestaña o limpiando los filtros.'}
                      </p>
                      {(grSearchQuery || grFilterStatus !== 'ALL' || grClientFilter !== 'ALL') && (
                        <button
                          type="button"
                          onClick={() => {
                            setGrSearchQuery('');
                            setGrFilterStatus('ALL');
                            setGrClientFilter('ALL');
                            setGrPage(1);
                          }}
                          className="mt-1 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-all cursor-pointer shadow-2xs"
                        >
                          Restablecer filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar for GR Confirmation Table */}
        {filteredGrFolios.length > 0 && (
          <BillingPagination
            currentPage={grPage}
            totalPages={totalGrPages}
            totalItems={filteredGrFolios.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setGrPage}
            label={grFilterStatus === 'CONFIRMED' ? 'folios facturados' : 'folios para validación GR'}
          />
        )}
      </div>
      )}

      {/* BOTTOM SECTION: PRE-FACTURAS Y FACTURAS EMITIDAS */}
      <div className="space-y-4">
        
        {/* FILTER BAR */}
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
          
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[280px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setInvPage(1);
              }}
              placeholder="Buscar factura por número, titular, concepto..."
              className="w-full bg-slate-50 pl-10 pr-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => {
                setSearchQuery('');
                setInvPage(1);
              }}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-all cursor-pointer border border-slate-200"
              title="Refrescar lista"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* PRE-INVOICES TABLE */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 z-10 shadow-2xs">
                <tr className="bg-slate-100/95 backdrop-blur-xs border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-3.5 px-4">Factura / Folio</th>
                  <th className="py-3.5 px-4">Cliente / Mercancía</th>
                  <th className="py-3.5 px-4">Concepto de Flete</th>
                  <th className="py-3.5 px-4 text-right">Monto Total</th>
                  <th className="py-3.5 px-4 text-center">Estado GR</th>
                  <th className="py-3.5 px-4 text-center">Reporte PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {paginatedInvoices.length > 0 ? (
                  paginatedInvoices.map((inv) => {
                    const relatedFolio = folios.find(f => f.id === inv.folioId);
                    const isGrConfirmed = relatedFolio ? relatedFolio.grStatus === 'CONFIRMADO' : (inv.status === 'Paid');
                    const folioTotal = relatedFolio 
                      ? relatedFolio.items.reduce((s, it) => s + (Number(it.totalMXN) || 0), 0)
                      : inv.totalMXN;
                    const displayTotal = folioTotal > 0 ? folioTotal : inv.totalMXN;

                    return (
                      <tr key={inv.id} className={`transition-colors ${isGrConfirmed ? 'bg-emerald-50/30 hover:bg-emerald-50/50' : 'hover:bg-slate-50/60'}`}>
                        
                        {/* Factura / Folio */}
                        <td className="py-4 px-4 font-black text-slate-900">
                          <div>{inv.id}</div>
                          <div className="text-[10px] text-slate-400 font-mono">Ref: {inv.folioId}</div>
                        </td>

                        {/* Cliente / Mercancia */}
                        <td className="py-4 px-4">
                          <div className="font-bold text-slate-900">{inv.clientName}</div>
                          <div className="text-[10px] text-slate-400 font-medium truncate max-w-[160px]">
                            Cargo: {inv.items[0]?.description || 'Partes mecánicas de ensamble'}
                          </div>
                        </td>

                        {/* Concepto de flete */}
                        <td className="py-4 px-4 max-w-xs text-slate-600">
                          <p className="text-[11px] leading-snug font-medium">
                            Recepción de Carga y Flete: {inv.items[0]?.description || 'Partes de ensamble industrial'}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <p className="text-[10px] text-slate-400 font-mono">
                              Emisión: {inv.issueDate}
                            </p>
                            {relatedFolio?.grBillingDate && (
                              <span className="text-[9px] font-black font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                                Fecha Facturación: {relatedFolio.grBillingDate}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Monto Total */}
                        <td className="py-4 px-4 text-right">
                          <div className="font-black text-slate-900 text-sm tracking-tight font-mono">
                            ${displayTotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-[10px] font-bold text-slate-400 font-mono">MXN</div>
                        </td>

                        {/* Estado GR */}
                        <td className="py-4 px-4 text-center">
                          {!isUserClient && relatedFolio ? (
                            <button
                              type="button"
                              onClick={() => handleToggleGrStatus(relatedFolio)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black bg-emerald-100 hover:bg-amber-100 text-emerald-800 hover:text-amber-900 border border-emerald-300 hover:border-amber-400 uppercase shadow-2xs transition-all cursor-pointer group"
                              title="Haga clic para desmarcar y regresar este folio a la tabla de GR POR FACTURAR"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 group-hover:hidden" />
                              <RotateCcw className="h-3.5 w-3.5 text-amber-700 hidden group-hover:inline" />
                              <span className="group-hover:hidden">GR FACTURADO</span>
                              <span className="hidden group-hover:inline">DESMARCAR (REGRESAR A POR FACTURAR)</span>
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase shadow-2xs">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                              <span>GR FACTURADO</span>
                            </span>
                          )}
                        </td>

                        {/* Reporte PDF */}
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => downloadPreInvoicePDF(inv)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] rounded-xl transition-all cursor-pointer border border-slate-200 flex items-center gap-1.5 mx-auto"
                            title="Descargar Reporte de Recepción / Flete"
                          >
                            <Download className="h-3.5 w-3.5 text-slate-500" />
                            <span>VER REPORTE</span>
                          </button>
                        </td>

                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-400 font-medium">
                      No hay registros de folios o fletes registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Bar for Invoices / Confirmed GR Table */}
          {filteredInvoices.length > 0 && (
            <BillingPagination
              currentPage={invPage}
              totalPages={totalInvPages}
              totalItems={filteredInvoices.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setInvPage}
              label="facturas / folios facturados"
            />
          )}
        </div>

      </div>

      {/* SAT CONSTANCIA MODAL */}
      {isSatModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 bg-gradient-to-r from-orange-600 to-amber-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <FileCheck className="h-5 w-5 text-white" />
                <h3 className="text-sm font-black uppercase">Constancia de Situación Fiscal (SAT)</h3>
              </div>
              <button 
                onClick={() => setIsSatModalOpen(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl space-y-1">
                <p className="font-bold text-slate-800">Cédula Fiscal Registrada:</p>
                <p className="font-mono text-slate-700">RFC: <strong className="text-slate-900">{clientRfc}</strong></p>
                <p className="text-slate-600">Razón Social: <strong>{clientName}</strong></p>
                <p className="text-slate-600">Régimen Fiscal: 601 - General de Ley Personas Morales</p>
                <p className="text-slate-600">Código Postal Fiscal: 78210</p>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-orange-500 transition-colors cursor-pointer bg-slate-50">
                <Upload className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="font-bold text-slate-800">Arrastra tu Constancia en formato PDF o Imagen</p>
                <p className="text-[11px] text-slate-400 mt-1">Máximo 10 MB • Documentos oficiales del SAT</p>
                <button 
                  onClick={() => {
                    alert('Constancia de Situación Fiscal cargada y validada correctamente ante el SAT.');
                    setIsSatModalOpen(false);
                  }}
                  className="mt-3 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-xs transition-all cursor-pointer"
                >
                  Seleccionar Archivo
                </button>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 text-right">
              <button
                onClick={() => setIsSatModalOpen(false)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold text-xs transition-all cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL & CONCILIATION MODAL */}
      {selectedInvoiceForDetail && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Receipt className="h-5 w-5 text-red-500" />
                <div>
                  <h3 className="text-sm font-black uppercase">Detalle & Conciliación de Prefactura</h3>
                  <p className="text-[10px] text-slate-400 font-mono">{selectedInvoiceForDetail.id} • Folio: {selectedInvoiceForDetail.folioId}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedInvoiceForDetail(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Cliente Receptor</span>
                  <span className="font-extrabold text-slate-900 text-sm">{selectedInvoiceForDetail.clientName}</span>
                </div>
                <div className="text-right">
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Fecha de Emisión</span>
                  <span className="font-extrabold text-slate-800">{selectedInvoiceForDetail.issueDate}</span>
                </div>
              </div>

              <div>
                <h4 className="font-extrabold text-slate-900 mb-2 uppercase text-[11px]">Partes y Conceptos Acreditados:</h4>
                <div className="space-y-2">
                  {selectedInvoiceForDetail.items.map((it, idx) => (
                    <div key={idx} className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-800">{it.description}</p>
                        <p className="text-[10px] text-slate-400">Cantidad: {it.quantity} PZA • Precio Unitario: ${Number(it.priceMXN).toLocaleString()} MXN</p>
                      </div>
                      <span className="font-black text-slate-900">${Number(it.totalMXN).toLocaleString()} MXN</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-slate-900 text-white rounded-xl flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Total Conciliado a Facturar</p>
                  <p className="text-lg font-black text-red-400">${selectedInvoiceForDetail.totalMXN.toLocaleString()} MXN</p>
                </div>
                <button
                  onClick={() => {
                    downloadPreInvoicePDF(selectedInvoiceForDetail);
                  }}
                  className="px-3.5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all cursor-pointer text-xs"
                >
                  <Download className="h-4 w-4" />
                  <span>Descargar PDF</span>
                </button>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setSelectedInvoiceForDetail(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold text-xs transition-all cursor-pointer"
              >
                Cerrar
              </button>
              {!isUserClient && (
                <button
                  onClick={() => {
                    handleEmitInvoice(selectedInvoiceForDetail);
                    setSelectedInvoiceForDetail(null);
                  }}
                  className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs"
                >
                  Confirmar Emitir CFDI
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DETAIL & STATUS CHANGE MODAL FOR DELIVERED FOLIO */}
      {selectedFolioForDetail && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Truck className="h-5 w-5 text-red-500" />
                <div>
                  <h3 className="text-sm font-black uppercase">Detalle & Cambio de Estado de Remisión</h3>
                  <p className="text-[10px] text-slate-400 font-mono">Folio ID: {selectedFolioForDetail.id}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedFolioForDetail(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Cliente</span>
                  <span className="font-extrabold text-slate-900 text-sm">{selectedFolioForDetail.clientName}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Destino / Planta</span>
                  <span className="font-extrabold text-slate-800">{selectedFolioForDetail.destination || 'PLANTA'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Estado Actual</span>
                  <span className="inline-block mt-0.5 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 border border-amber-300">
                    {selectedFolioForDetail.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              {/* GR Validation Control Box */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-xs font-black text-slate-900 uppercase">
                      Confirmación de Recepción de Mercancía (GR):
                    </label>
                    <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                      {selectedFolioForDetail.grStatus === 'CONFIRMADO' 
                        ? 'Proceso finalizado. Recepción confirmada y facturada.'
                        : 'Por facturar. Seleccione la fecha y la casilla para validar.'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-black uppercase flex items-center gap-1.5 ${
                    selectedFolioForDetail.grStatus === 'CONFIRMADO' 
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                      : 'bg-amber-100 text-amber-800 border border-amber-300'
                  }`}>
                    {selectedFolioForDetail.grStatus === 'CONFIRMADO' ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        <span>GR FACTURADO</span>
                      </>
                    ) : (
                      <>
                        <Clock className="h-3.5 w-3.5 text-amber-600" />
                        <span>GR POR FACTURAR</span>
                      </>
                    )}
                  </span>
                </div>

                {/* Date for Billing */}
                <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1.5 shadow-2xs">
                  <label className="block text-[11px] font-bold text-slate-700 uppercase flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-brand-red" />
                      <span>Fecha a la que corresponde el GR para Facturación:</span>
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono font-normal">
                      Mes de Facturación
                    </span>
                  </label>
                  <input
                    type="date"
                    value={selectedFolioForDetail.grBillingDate || (selectedFolioForDetail.shipmentDate || selectedFolioForDetail.createdDate || '').substring(0, 10) || new Date().toISOString().split('T')[0]}
                    onChange={(e) => handleUpdateGrBillingDate(selectedFolioForDetail, e.target.value)}
                    className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-2xs w-full cursor-pointer"
                  />
                  <p className="text-[10px] text-slate-500 font-medium">
                    {selectedFolioForDetail.grStatus === 'CONFIRMADO'
                      ? '✓ Folio registrado en el mes de facturación de esta fecha.'
                      : 'ℹ️ Si este folio queda por facturar, se arrastrará al siguiente mes para su confirmación y facturación.'}
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => handleToggleGrStatus(selectedFolioForDetail)}
                    className={`w-full py-2.5 px-4 rounded-xl font-black text-xs transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs ${
                      selectedFolioForDetail.grStatus === 'CONFIRMADO'
                        ? 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFolioForDetail.grStatus === 'CONFIRMADO'}
                      onChange={() => {}} // Handled by parent button click
                      className="h-4 w-4 rounded accent-emerald-600"
                    />
                    <span>
                      {selectedFolioForDetail.grStatus === 'CONFIRMADO' ? 'Cambiar a GR POR FACTURAR' : 'Validar y Cambiar a GR FACTURADO'}
                    </span>
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-extrabold text-slate-900 mb-2 uppercase text-[11px]">Mercancía / Piezas Procesadas:</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {selectedFolioForDetail.items.map((it, idx) => (
                    <div key={idx} className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-800">{it.description}</p>
                        <p className="text-[10px] text-slate-400">Cantidad: {it.quantity} PZA • Peso: {it.totalWeightKg || 'N/D'} kg</p>
                      </div>
                      <span className="font-black text-slate-900">
                        ${Number(it.totalMXN || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-slate-900 text-white rounded-xl flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Importe Total Estimado de Flete</p>
                  <p className="text-lg font-black text-red-400">
                    ${selectedFolioForDetail.items.reduce((acc, it) => acc + (Number(it.totalMXN) || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                  </p>
                </div>
                <button
                  onClick={() => {
                    downloadFolioPDF(selectedFolioForDetail);
                  }}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all cursor-pointer text-xs border border-slate-700"
                >
                  <Download className="h-4 w-4" />
                  <span>Descargar PDF Remisión</span>
                </button>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setSelectedFolioForDetail(null)}
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
